import React, { useContext } from "react";
import { useIntl } from "react-intl";
import OwButton from "../../shared/OwButton/OwButton";
import OwLabel from "../../shared/OwLabel/OwLabel";
import styled from "styled-components";
import HelperService from "../../../services/HelperService";
import { useCart } from "../../../contexts/CartContext";
import { useAppContextNonNull } from "@contexts/AppContext";
import CatalogService from "@services/CatalogService";
import CurrenciesEnum from "@models/CurrenciesEnum";
import UserService from "@services/UserService";
import MinimumOrderService from "@services/MinimumOrderService";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";

type Props = {
    proceedToCheckout: () => void;
};

export const EcommerceRightSideHeader: React.FC<Props> = (props) => {
    const { supplierModel, userModel, vendorModel } = useAppContextNonNull();
    const { cart } = useCart();
    const intl = useIntl();

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const currency = vendorModel.Catalog?.currency_id ? CatalogService.getCurrencyNameFromId(vendorModel.Catalog.currency_id) : "USD";

    // Supplier minimum order total: checkout is not reachable below it (the API would reject the payment).
    // This header lives in the fixed-height app header, so the notice overlays the (disabled) checkout button
    // in a single line instead of adding a row; the full sentence is the tooltip.
    const minOrder = MinimumOrderService.evaluateCart(supplierModel, cart);
    const isBelowMinOrder = !!minOrder && !minOrder.met;

    let minOrderOverlay: React.JSX.Element | null = null;

    if (isBelowMinOrder && minOrder) {
        const amounts = {
            minimum: HelperService.formatCurrency({ value: minOrder.minimum, currency }),
            shortfall: HelperService.formatCurrency({ value: minOrder.shortfall, currency }),
        };

        const fullMessage = hidePricing
            ? intl.formatMessage({ id: "error_min_order_total_not_met" })
            : intl.formatMessage({ id: "min_order_total_not_met" }, amounts);

        minOrderOverlay = (
            <MinOrderOverlay role={"status"} title={fullMessage} aria-label={fullMessage}>
                <i className={"fas fa-exclamation-triangle me-1"} aria-hidden={true} />
                <span className={"min-order-text"}>
                    {hidePricing ? (
                        <OwTranslate id={"min_order_total_below_minimum"} defaultMessage={"Below order minimum"} />
                    ) : (
                        <OwTranslate
                            id={"min_order_total_add_more"}
                            defaultMessage={"Add {shortfall} more"}
                            values={{ shortfall: amounts.shortfall }}
                        />
                    )}
                </span>
            </MinOrderOverlay>
        );
    }

    return (
        <EcommerceRightSideHeaderDiv>
            {cart?.Cart_Items?.length ? (
                <CheckoutButtonWrapper>
                    <OwButton title={"proceed_to_checkout"} fn={props.proceedToCheckout} disabled={isBelowMinOrder} />
                    {minOrderOverlay}
                </CheckoutButtonWrapper>
            ) : null}
            {!hidePricing ? (
                <div>
                    <OwLabel title={"sub_total"} postText={":"} isBold={true} />{" "}
                    {HelperService.formatCurrency({
                        value: cart?.sub_total || 0,
                        currency,
                        precision: supplierModel.pricing_precision || 2,
                    })}
                </div>
            ) : null}
        </EcommerceRightSideHeaderDiv>
    );
};

const CheckoutButtonWrapper = styled.div`
    position: relative;
    max-width: 100%;
`;

/** Covers the disabled checkout button exactly; one line, ellipsized if a long translation or currency won't fit. */
const MinOrderOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.5rem;
    border-radius: var(--bs-border-radius, 0.375rem);
    background-color: var(--bs-warning, #ffc107);
    color: #212529;
    font-size: 0.875rem;
    font-weight: 600;
    white-space: nowrap;
    cursor: not-allowed;

    .min-order-text {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const EcommerceRightSideHeaderDiv = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-content: center;
    align-items: center;
    justify-content: center;
`;
